import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  createEmployee,
  listAttendanceEvents,
  listDisciplinaryActions,
  listDivisions,
  listEmployees,
  listLeaveRequests,
  listShiftSwaps,
  listTardiness,
  updateEmployee,
} from '../../api/hr';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Pagination,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { AttendanceEvent, DisciplinaryAction, Division, Employee, LeaveRequest, ShiftSwap, Tardiness } from '../../types/domain';

const EMPLOYEE_STATUSES: Employee['status'][] = ['ACTIVE', 'ON_LEAVE', 'TERMINATED'];

/**
 * Employee Master (FASE 9, migration 0040). employees carries employee_no,
 * legal_name, branch_id, division_id, status only — no job title, no
 * contact fields, no join date beyond created_at (schema doesn't have
 * them; nothing here is fabricated). USER != EMPLOYEE is preserved: this
 * page never reads/writes `users`, only `employees` and, on the detail
 * drawer, a read-only `user_employee_links` lookup shown as a distinct
 * linked record, never merged into the employee's own fields.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md NEEDS_DECISION log.
 */
export default function EmployeesPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId, branches } = useBranch();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [branchFilter, setBranchFilter] = useState('ALL');
  const [selected, setSelected] = useState<Employee | null>(null);

  const canCreate = hasPermission('employee', 'create');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [empRows, divRows] = await Promise.all([listEmployees(), listDivisions()]);
      setEmployees(empRows);
      setDivisions(divRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  // Employee master is tenant-wide (employees.branch_id is a filterable
  // attribute, but the roster itself spans all branches an authorized
  // role can see) — no re-fetch on Cabang Aktif; the in-page branch filter
  // below narrows the already-loaded tenant-wide list instead.
  useEffect(() => {
    void load();
  }, []);

  const divisionName = (id: string | null) => (id ? divisions.find((d) => d.division_id === id)?.name ?? '—' : '—');
  const branchName = (id: string | null) => (id ? branches.find((b) => b.branch_id === id)?.name ?? id : '—');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => {
      if (q && !e.legal_name.toLowerCase().includes(q) && !e.employee_no.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && e.status !== statusFilter) return false;
      if (branchFilter !== 'ALL' && e.branch_id !== branchFilter) return false;
      return true;
    });
  }, [employees, search, statusFilter, branchFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  const byBranch = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of employees) {
      if (!e.branch_id) continue;
      m.set(e.branch_id, (m.get(e.branch_id) ?? 0) + 1);
    }
    return m;
  }, [employees]);

  return (
    <div>
      <PageHeader title="Employee Master" subtitle="Roster karyawan — dasar untuk kehadiran, cuti, tukar shift, dan disiplin. Migrasi 0040." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ Karyawan baru'}
        </Button>
      )}
      {showForm && canCreate && activeBranchId && (
        <NewEmployeeForm
          defaultBranchId={activeBranchId}
          branches={branches}
          divisions={divisions}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={(() => {
              const c = statusCounts(employees, EMPLOYEE_STATUSES);
              return [
                { label: 'Total Karyawan', value: employees.length },
                { label: 'Aktif', value: c.ACTIVE, tone: 'success' as const },
                { label: 'Cuti', value: c.ON_LEAVE, tone: 'warning' as const },
                { label: 'Berhenti', value: c.TERMINATED, tone: 'neutral' as const },
                { label: 'Cabang', value: byBranch.size, tone: 'info' as const },
              ];
            })()}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari No./Nama karyawan..."
            resultCount={total}
            filters={
              <>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 140 }}>
                  <option value="ALL">Semua status</option>
                  <option value="ACTIVE">Aktif</option>
                  <option value="ON_LEAVE">Cuti</option>
                  <option value="TERMINATED">Berhenti</option>
                </Select>
                <Select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} style={{ width: 160 }}>
                  <option value="ALL">Semua cabang</option>
                  {branches.map((b) => (
                    <option key={b.branch_id} value={b.branch_id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </>
            }
          />
          {employees.length === 0 && <EmptyState text="Belum ada karyawan." />}
          {employees.length > 0 && total === 0 && <EmptyState text="Tidak ada karyawan yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>No. Karyawan</th>
                  <th style={thStyle}>Nama</th>
                  <th style={thStyle}>Cabang</th>
                  <th style={thStyle}>Divisi</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((e) => (
                  <ClickableRow key={e.employee_id} onClick={() => setSelected(e)}>
                    <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{e.employee_no}</td>
                    <td style={tdStyle}>{e.legal_name}</td>
                    <td style={tdStyle}>{branchName(e.branch_id)}</td>
                    <td style={tdStyle}>{divisionName(e.division_id)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={e.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <EmployeeDetailDrawer
        employee={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        divisions={divisions}
        branches={branches}
        canEdit={canCreate}
        onChanged={() => void load()}
      />
    </div>
  );
}

function NewEmployeeForm({
  defaultBranchId,
  branches,
  divisions,
  onCreated,
}: {
  defaultBranchId: string;
  branches: { branch_id: string; name: string }[];
  divisions: Division[];
  onCreated: () => void;
}) {
  const [employeeNo, setEmployeeNo] = useState('');
  const [legalName, setLegalName] = useState('');
  const [branchId, setBranchId] = useState(defaultBranchId);
  const [divisionId, setDivisionId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createEmployee(employeeNo.trim(), legalName.trim(), branchId, divisionId || null);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 140 }}>
        <FieldLabel label="No. Karyawan">
          <TextInput value={employeeNo} onChange={(e) => setEmployeeNo(e.target.value)} required autoFocus />
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Nama Lengkap">
          <TextInput value={legalName} onChange={(e) => setLegalName(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Cabang">
          <Select value={branchId} onChange={(e) => setBranchId(e.target.value)} required>
            {branches.map((b) => (
              <option key={b.branch_id} value={b.branch_id}>
                {b.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Divisi (opsional)">
          <Select value={divisionId} onChange={(e) => setDivisionId(e.target.value)}>
            <option value="">—</option>
            {divisions.map((d) => (
              <option key={d.division_id} value={d.division_id}>
                {d.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function EmployeeDetailDrawer({
  employee,
  open,
  onClose,
  divisions,
  branches,
  canEdit,
  onChanged,
}: {
  employee: Employee | null;
  open: boolean;
  onClose: () => void;
  divisions: Division[];
  branches: { branch_id: string; name: string }[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { hasPermission } = usePermissions();
  const [tab, setTab] = useState<'info' | 'attendance' | 'leave' | 'swap' | 'disciplinary'>('info');
  const [attendance, setAttendance] = useState<AttendanceEvent[] | null>(null);
  const [tardiness, setTardiness] = useState<Tardiness[] | null>(null);
  const [leave, setLeave] = useState<LeaveRequest[] | null>(null);
  const [swaps, setSwaps] = useState<ShiftSwap[] | null>(null);
  const [disciplinary, setDisciplinary] = useState<DisciplinaryAction[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [status, setStatus] = useState<Employee['status']>('ACTIVE');
  const [savingStatus, setSavingStatus] = useState(false);

  const canReadDisciplinary = hasPermission('disciplinary_action', 'read');

  useEffect(() => {
    setAttendance(null);
    setTardiness(null);
    setLeave(null);
    setSwaps(null);
    setDisciplinary(null);
    setTab('info');
    if (!employee) return;
    setStatus(employee.status);
    // Lazy-loaded only when the drawer opens — never fetched for the whole
    // list. Filtered client-side to this one employee since these
    // adapters are branch-scoped, not employee-scoped, and the roster is
    // already tenant-wide here.
    Promise.all([
      listAttendanceEvents(employee.branch_id ?? undefined),
      listTardiness(employee.branch_id ?? undefined),
      listLeaveRequests(employee.branch_id ?? undefined),
      listShiftSwaps(employee.branch_id ?? undefined),
      canReadDisciplinary ? listDisciplinaryActions(employee.branch_id ?? undefined) : Promise.resolve([]),
    ])
      .then(([att, tardy, lv, sw, disc]) => {
        setAttendance(att.filter((a) => a.employee_id === employee.employee_id));
        setTardiness(tardy.filter((t) => t.employee_id === employee.employee_id));
        setLeave(lv.filter((l) => l.employee_id === employee.employee_id));
        setSwaps(sw.filter((s) => s.requesting_employee_id === employee.employee_id || s.covering_employee_id === employee.employee_id));
        setDisciplinary(disc.filter((d) => d.employee_id === employee.employee_id));
      })
      .catch((e) => setLoadErr(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.employee_id, canReadDisciplinary]);

  if (!employee) return null;

  const saveStatus = async () => {
    setSavingStatus(true);
    try {
      await updateEmployee(employee.employee_id, employee.legal_name, employee.branch_id ?? '', employee.division_id, status);
      onChanged();
      onClose();
    } catch (err) {
      setLoadErr(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingStatus(false);
    }
  };

  const tabs: Array<{ key: typeof tab; label: string }> = [
    { key: 'info', label: 'Info' },
    { key: 'attendance', label: 'Kehadiran' },
    { key: 'leave', label: 'Cuti' },
    { key: 'swap', label: 'Tukar Shift' },
    ...(canReadDisciplinary ? [{ key: 'disciplinary' as const, label: 'Disiplin' }] : []),
  ];

  return (
    <Drawer open={open} onClose={onClose} title={employee.legal_name}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid var(--alex-border)', flexWrap: 'wrap' }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              border: 'none',
              background: 'none',
              padding: '8px 10px',
              fontSize: 'var(--alex-fs-caption)',
              fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? 'var(--alex-primary)' : 'var(--alex-text-secondary)',
              borderBottom: tab === t.key ? '2px solid var(--alex-primary)' : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {loadErr && <ErrorNote text={loadErr} />}

      {tab === 'info' && (
        <>
          <DrawerField label="No. Karyawan" value={employee.employee_no} />
          <DrawerField label="Cabang" value={branches.find((b) => b.branch_id === employee.branch_id)?.name ?? '—'} />
          <DrawerField label="Divisi" value={divisions.find((d) => d.division_id === employee.division_id)?.name ?? 'Belum tersedia'} />
          <DrawerField label="Tercatat sejak" value={formatDateTime(employee.created_at)} />
          <DrawerField label="Status" value={<StatusBadge status={employee.status} />} />
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel label="Ubah status">
                  <Select value={status} onChange={(e) => setStatus(e.target.value as Employee['status'])}>
                    <option value="ACTIVE">Aktif</option>
                    <option value="ON_LEAVE">Cuti</option>
                    <option value="TERMINATED">Berhenti</option>
                  </Select>
                </FieldLabel>
              </div>
              <Button variant="secondary" onClick={() => void saveStatus()} loading={savingStatus}>
                Simpan
              </Button>
            </div>
          )}
          <p style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', marginTop: 16 }}>
            Data kompensasi (payroll) dan jabatan/posisi tidak tersedia di skema saat ini — tidak ditampilkan di sini.
          </p>
        </>
      )}

      {tab === 'attendance' && (
        <>
          {attendance === null && <LoadingNote />}
          {attendance && attendance.length === 0 && <EmptyState text="Belum ada kehadiran tercatat." variant="compact" />}
          {attendance && attendance.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tipe</th>
                  <th style={thStyle}>Waktu</th>
                </tr>
              </thead>
              <tbody>
                {attendance.map((a) => (
                  <tr key={a.event_id}>
                    <td style={tdStyle}>{a.event_type === 'CLOCK_IN' ? 'Masuk' : 'Pulang'}</td>
                    <td style={tdStyle}>{formatDateTime(a.event_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {tardiness && tardiness.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-primary)', marginBottom: 6 }}>Keterlambatan</div>
              {tardiness.map((t) => (
                <div key={t.tardiness_id} style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-danger)' }}>
                  Terlambat {t.late_minutes} menit — {formatDateTime(t.created_at)}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'leave' && (
        <>
          {leave === null && <LoadingNote />}
          {leave && leave.length === 0 && <EmptyState text="Belum ada riwayat cuti." variant="compact" />}
          {leave && leave.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Jenis</th>
                  <th style={thStyle}>Tanggal</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {leave.map((l) => (
                  <tr key={l.leave_id}>
                    <td style={tdStyle}>{l.leave_type}</td>
                    <td style={tdStyle}>{l.date_from} – {l.date_to}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={l.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {tab === 'swap' && (
        <>
          {swaps === null && <LoadingNote />}
          {swaps && swaps.length === 0 && <EmptyState text="Belum ada riwayat tukar shift." variant="compact" />}
          {swaps && swaps.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Peran</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {swaps.map((s) => (
                  <tr key={s.swap_id}>
                    <td style={tdStyle}>{s.requesting_employee_id === employee.employee_id ? 'Peminta' : 'Pengganti'}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={s.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {tab === 'disciplinary' && canReadDisciplinary && (
        <>
          {disciplinary === null && <LoadingNote />}
          {disciplinary && disciplinary.length === 0 && <EmptyState text="Tidak ada tindakan disiplin." variant="compact" />}
          {disciplinary && disciplinary.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Jenis</th>
                  <th style={thStyle}>Tanggal</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {disciplinary.map((d) => (
                  <tr key={d.action_id}>
                    <td style={tdStyle}>{d.action_type}</td>
                    <td style={tdStyle}>{formatDateTime(d.issued_at)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={d.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </Drawer>
  );
}
